import { useApi } from '@/api';
import {
  Banner,
  KpiCard,
  LinkButton,
  MultipleKpiCard,
  Page,
  PageSkeleton
} from '@/components';
import { useSearchParams } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';
import {
  CurrencyValue,
  Flex,
  FormattedNumber,
  Heading,
  PercentageValue,
  Box
} from '@blockanalitica/ui';
import BuybackEventsTable from './BuybackEventsTable';
import HistoricTabs from './HistoricTabs';
import SkySupplyDistribution from './SkySupplyDistribution';

export default function BuybackPage() {
  const [daysAgo] = useSearchParams('days_ago', '1');
  const [data, error] = useApi({
    url: '/buyback/',
    queryParams: {
      days_ago: daysAgo
    }
  });

  if (error) {
    throw error;
  }

  if (data) {
    return (
      <Page title="Buyback">
        <Banner>
          <Box paddingTop="xs" paddingBottom="m" maxWidth="85%">
            <Heading size="2">About the Sky Protocol Buyback</Heading>
            <br />
            Explore Sky&apos;s buyback program, powered by the Smart Burn
            Engine. This onchain module uses protocol surplus revenue to buyback
            SKY from the open market, at a rate controlled by Sky Governance and
            risk teams. This page offers a breakdown of the cumulative buyback
            volume, average prices and profitability over a time series.
          </Box>
        </Banner>
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <KpiCard
            title="Total SKY buyback"
            data={data}
            field="sky_balance"
            options={{
              valueOptions: {
                currencyValueOptions: {
                  iconSize: '3',
                  currency: tokensMapping('SKY')
                }
              }
            }}
          />
          <KpiCard
            title="SKY buyback in last 24h"
            data={data}
            field="sky_buyback_24h"
            options={{
              valueOptions: {
                currencyValueOptions: {
                  iconSize: '3',
                  currency: tokensMapping('SKY')
                }
              }
            }}
          />
        </Flex>
        <HistoricTabs />
        <Flex gap={['m', 's']} width={['100%']} alignItems="center">
          <MultipleKpiCard
            title="Total USDS spent on buyback"
            value={
              <CurrencyValue
                variant="dynamic"
                value={data.usds_spent_cumulative}
                options={{
                  currency: tokensMapping('USDS'),
                  notation: 'standard'
                }}
              />
            }
            footer={[
              {
                title: 'Percentage of circulating supply',
                value: <PercentageValue value={data.percentage_supply_held} />
              },
              {
                title: 'SKY avg buyback price',
                value: (
                  <FormattedNumber
                    value={data.sky_avg_price}
                    options={{
                      formatNumberOptions: {
                        notation: 'standard',
                        maximumFractionDigits: 4
                      },
                      suffix: (
                        <Flex color="muted" fontSize="-2">
                          USDS/SKY
                        </Flex>
                      )
                    }}
                  />
                )
              },
              {
                title: 'SKY current price',
                value: (
                  <FormattedNumber
                    value={data.sky_price}
                    options={{
                      formatNumberOptions: {
                        notation: 'standard',
                        maximumFractionDigits: 4
                      },
                      suffix: (
                        <Flex color="muted" fontSize="-2">
                          USDS/SKY
                        </Flex>
                      )
                    }}
                  />
                )
              }
            ]}
          />
        </Flex>

        {/* <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <KpiCard
            title="Net profit/loss"
            data={data}
            field="net_profit"
            options={{
              valueOptions: {
                currencyValueOptions: {
                  currency: 'USD',
                  trend: true,
                  trendIcon: false
                }
              }
            }}
            footer={
              <Flex gap="xs">
                <Text>Margin:</Text>
                <PercentageValue value={data.net_profit_ratio} />
              </Flex>
            }
          />
          <LinkWrapper
            to="/treasury"
            // @ts-ignore
            as={Link}
            variant="kpiCardLinkWithBlendMode">
            <KpiCard
              title="Sky Treasury"
              data={data}
              field="total_balance_usd"
              options={{
                notation: 'compact',
                currency: 'USD',
                linkArrow: true
              }}
            />
          </LinkWrapper>
        </Flex> */}
        <SkySupplyDistribution />

        <Flex justifyContent="space-between">
          <Heading size="3">Latest Buybacks</Heading>
          <LinkButton variant="secondary" to="/buyback/activity/">
            view all
          </LinkButton>
        </Flex>
        <BuybackEventsTable />
      </Page>
    );
  }
  return <PageSkeleton />;
}
